import React, { ReactNode, MouseEvent, HTMLProps } from 'react'
import classNames from 'classnames/bind'
import { ShieldFilled } from '@babylon/icons'

import Avatar, { AvatarSize } from '../Avatar'
import { IMedkitComponent, Text } from '..'
import styles from './index.module.scss'
import { profileTheme } from './theme'

const cx = classNames.bind(styles)

export interface ProfileProps extends HTMLProps<HTMLDivElement> {
  /** Remove the top border */
  noTopBorder?: boolean
  /** Remove the bottom border */
  noBottomBorder?: boolean
  /** React element to be placed before the title, generally some text */
  beforeTitle?: ReactNode
  /** Title */
  title: string
  /** Text to be placed after the title */
  subtitle?: string
  /** Should show CTA */
  shouldShowCTA?: boolean
  /** Callback when CTA button is clicked */
  onClickCTA?(e: MouseEvent<HTMLDivElement>): void
  /** URL of the avatar */
  image?: string
  /** Should display the Account Page design */
  isAccountPage?: boolean
  /** Should show verified badge */
  showVerifiedBadge?: boolean
  /** Should show loading state */
  loading?: boolean
  /** Call To Action element */
  callToAction?: ReactNode
  /** Size of the avatar */
  avatarSize?: AvatarSize
  /** Text of the default CTA */
  editMessage?: string
}

const Profile: IMedkitComponent<ProfileProps, typeof profileTheme> = ({
  noTopBorder = false,
  noBottomBorder = false,
  className,
  beforeTitle,
  title = '',
  subtitle = '',
  shouldShowCTA = false,
  onClickCTA = () => {},
  image,
  isAccountPage,
  showVerifiedBadge,
  loading = false,
  callToAction,
  avatarSize = AvatarSize.Large,
  editMessage,
}: ProfileProps) => (
  <div
    className={cx(
      styles.profileSummary,
      { 'profileSummary--withTopBorder': !noTopBorder },
      { 'profileSummary--withBottomBorder': !noBottomBorder },
      className
    )}
  >
    <div className={cx(styles.infoWrapper, 'wrapper')}>
      <Avatar
        className={styles.infoWrapper__avatar}
        src={image}
        description={isAccountPage ? subtitle : title}
        size={avatarSize}
        fallbackText={(isAccountPage ? subtitle : title).substr(0, 1)}
        highlightIcon
        icon={
          showVerifiedBadge && (
            <ShieldFilled
              className={styles.infoWrapper__avatarIcon}
              title="Verified"
            />
          )
        }
        fallbackClassName={styles.profileSummary__avatar}
      />
      {isAccountPage && (subtitle || loading) && (
        <Text
          variant="h1"
          className={cx(styles.account__heading, {
            'profileSummary__heading--loading': loading,
            'profileSummary__headingAccount--loading': loading,
          })}
        >
          {subtitle}
        </Text>
      )}
      {!isAccountPage && (
        <Text variant="h2" className={cx(styles.profileSummary__heading)}>
          {beforeTitle && (
            <Text
              variant="body"
              tag="span"
              className={styles.profileSummary__beforeTitle}
            >
              {beforeTitle}
            </Text>
          )}
          <div
            className={cx({
              'profileSummary__heading--loading': loading,
            })}
          >
            {title}
          </div>
          {subtitle && (
            <Text
              variant="body"
              tag="span"
              className={styles.profileSummary__subHeading}
            >
              {subtitle}
            </Text>
          )}
        </Text>
      )}
    </div>
    {shouldShowCTA && (
      <div
        role="button"
        onClick={(e) => onClickCTA(e)}
        tabIndex={0}
        data-testid="cta-profile"
      >
        {!callToAction ? (
          <button type="button" className={cx(styles.edit, 'editButton')}>
            {editMessage}
          </button>
        ) : (
          callToAction
        )}
      </div>
    )}
  </div>
)

Profile.theme = profileTheme

export default Profile
